import { getSupabaseBrowserClient } from '../lib/supabase'
import { toCustomerSafeRpcError } from './customer-safe-operation-error'

export {
  customerSafeOperationMessage,
  GENERIC_CUSTOMER_OPERATION_ERROR,
  toCustomerSafeRpcError,
  CustomerSafeRpcError,
} from './customer-safe-operation-error'

type RpcError = { message: string; code?: string; details?: string | null; hint?: string | null }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse

function object(value: unknown): Record<string, unknown> { return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {} }
function objects(value: unknown): Record<string, unknown>[] { return Array.isArray(value) ? value.map(object) : [] }
function text(value: unknown): string { return typeof value === 'string' ? value : '' }
function nullableText(value: unknown): string | null { return typeof value === 'string' && value ? value : null }
function booleanValue(value: unknown): boolean { return value === true || value === 'true' }
function numberValue(value: unknown): number { return typeof value === 'number' ? value : Number(value) || 0 }

async function invoke(functionName: string, args: Record<string, unknown> = {}) {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc(functionName, args)
  if (error) {
    console.error(`Supabase RPC ${functionName} failed`, error)
    throw toCustomerSafeRpcError(functionName, error)
  }
  return data
}

export type AssessmentOption = { code: string; label: string }
export type AssessmentAnswer = {
  answerCode: string | null
  answerDisplay: string | null
  answerValue: unknown
  notes: string | null
  responseSource: string
  sourceSnapshot: Record<string, unknown>
  updatedAt: string | null
}
export type AssessmentQuestion = {
  questionCode: string
  questionVersionId: string
  title: string
  prompt: string
  helpText: string | null
  answerType: string
  moduleCode: string
  displayOrder: number
  options: AssessmentOption[]
  branchMetadata: Record<string, unknown>
  answer: AssessmentAnswer | null
}
export type AssessmentConfirmation = {
  id: string
  confirmationType: string
  confirmationKind: string
  specialty: string | null
  reason: string
  requiredAuthority: string
  status: string
  assignedMembershipId: string | null
  requestedAt: string | null
  completedAt: string | null
  dueAt: string | null
  sourceSnapshot: Record<string, unknown>
  sourceRuleCondition: Record<string, unknown>
  sourceRuleExplanation: string | null
  deepLinkPath: string
  openInformationRequests: number
  sourceRuleVersionId: string | null
  sourceRuleCode: string | null
}
export type AssessmentWorkspace = {
  aiUseCase: { id: string; title: string; businessPurpose: string; operationalState: string; organisationId: string }
  assessment: null | {
    id: string
    cycleNo: number
    status: string
    frameworkVersionId: string
    reason: string | null
    reassessmentSourceType: string | null
    reassessmentSourceId: string | null
    ownerInputCompletedAt: string | null
    startedAt: string | null
    submittedAt: string | null
    evaluatedAt: string | null
    finalisedAt: string | null
  }
  frameworkAvailable: boolean
  questions: AssessmentQuestion[]
  confirmations: AssessmentConfirmation[]
  readiness: { ownerInputComplete: boolean; openConfirmations: number; unassignedConfirmations: number; canSubmit: boolean }
}

function answer(value: unknown): AssessmentAnswer | null {
  if (!value || typeof value !== 'object') return null
  const row = object(value)
  return {
    answerCode: nullableText(row.answerCode),
    answerDisplay: nullableText(row.answerDisplay),
    answerValue: row.answerValue,
    notes: nullableText(row.notes),
    responseSource: text(row.responseSource),
    sourceSnapshot: object(row.sourceSnapshot),
    updatedAt: nullableText(row.updatedAt),
  }
}

export async function getAssessmentWorkspace(aiUseCaseId: string): Promise<AssessmentWorkspace> {
  const row = object(await invoke('get_r1_assessment_workspace', { p_ai_use_case_id: aiUseCaseId }))
  const useCase = object(row.aiUseCase)
  const assessmentRow = row.assessment ? object(row.assessment) : null
  const readiness = object(row.readiness)
  return {
    aiUseCase: {
      id: text(useCase.id), title: text(useCase.title), businessPurpose: text(useCase.businessPurpose),
      operationalState: text(useCase.operationalState), organisationId: text(useCase.organisationId),
    },
    assessment: assessmentRow ? {
      id: text(assessmentRow.id), cycleNo: numberValue(assessmentRow.cycleNo), status: text(assessmentRow.status),
      frameworkVersionId: text(assessmentRow.frameworkVersionId), reason: nullableText(assessmentRow.reason),
      reassessmentSourceType: nullableText(assessmentRow.reassessmentSourceType), reassessmentSourceId: nullableText(assessmentRow.reassessmentSourceId),
      ownerInputCompletedAt: nullableText(assessmentRow.ownerInputCompletedAt), startedAt: nullableText(assessmentRow.startedAt),
      submittedAt: nullableText(assessmentRow.submittedAt), evaluatedAt: nullableText(assessmentRow.evaluatedAt),
      finalisedAt: nullableText(assessmentRow.finalisedAt),
    } : null,
    frameworkAvailable: booleanValue(row.frameworkAvailable),
    questions: objects(row.questions).map((question) => ({
      questionCode: text(question.questionCode), questionVersionId: text(question.questionVersionId), title: text(question.title),
      prompt: text(question.prompt), helpText: nullableText(question.helpText), answerType: text(question.answerType),
      moduleCode: text(question.moduleCode), displayOrder: numberValue(question.displayOrder),
      options: objects(question.options).map((option) => ({ code: text(option.code), label: text(option.label) })),
      branchMetadata: object(question.branchMetadata), answer: answer(question.answer),
    })),
    confirmations: objects(row.confirmations).map((confirmation) => ({
      id: text(confirmation.id), confirmationType: text(confirmation.confirmationType), confirmationKind: text(confirmation.confirmationKind),
      specialty: nullableText(confirmation.specialty), reason: text(confirmation.reason), requiredAuthority: text(confirmation.requiredAuthority),
      status: text(confirmation.status), assignedMembershipId: nullableText(confirmation.assignedMembershipId),
      requestedAt: nullableText(confirmation.requestedAt), completedAt: nullableText(confirmation.completedAt),
      dueAt: nullableText(confirmation.dueAt), sourceSnapshot: object(confirmation.sourceSnapshot),
      sourceRuleCondition: object(confirmation.sourceRuleCondition), sourceRuleExplanation: nullableText(confirmation.sourceRuleExplanation),
      deepLinkPath: text(confirmation.deepLinkPath), openInformationRequests: numberValue(confirmation.openInformationRequests),
      sourceRuleVersionId: nullableText(confirmation.sourceRuleVersionId), sourceRuleCode: nullableText(confirmation.sourceRuleCode),
    })),
    readiness: {
      ownerInputComplete: booleanValue(readiness.ownerInputComplete), openConfirmations: numberValue(readiness.openConfirmations),
      unassignedConfirmations: numberValue(readiness.unassignedConfirmations), canSubmit: booleanValue(readiness.canSubmit),
    },
  }
}

export async function startAssessment(aiUseCaseId: string) {
  return object(await invoke('start_r1_assessment', { p_ai_use_case_id: aiUseCaseId }))
}

export async function saveAssessmentResponse(
  assessmentId: string,
  questionVersionId: string,
  answerValue: unknown,
  answerCode: string | null,
  answerDisplay: string | null,
  notes: string | null = null,
) {
  return invoke('save_phase3_assessment_response', {
    p_assessment_id: assessmentId,
    p_question_version_id: questionVersionId,
    p_answer_value: answerValue,
    p_answer_code: answerCode,
    p_answer_display: answerDisplay,
    p_notes: notes,
  })
}

export async function completeOwnerInput(assessmentId: string) {
  return object(await invoke('complete_r1_owner_input', { p_assessment_id: assessmentId }))
}

export async function assignConfirmation(requirementId: string, membershipId: string) {
  return object(await invoke('assign_r1_confirmation', { p_requirement_id: requirementId, p_membership_id: membershipId }))
}

export async function submitAssessmentWhenReady(assessmentId: string) {
  return object(await invoke('submit_r1_assessment_when_ready', { p_assessment_id: assessmentId }))
}

export type ConfirmationTask = {
  requirement: Record<string, unknown>
  responses: Record<string, unknown>[]
  informationRequests: Record<string, unknown>[]
}

export async function getConfirmationTask(requirementId: string): Promise<ConfirmationTask> {
  const row = object(await invoke('get_r1_confirmation_task', { p_requirement_id: requirementId }))
  return { requirement: object(row.requirement), responses: objects(row.responses), informationRequests: objects(row.informationRequests) }
}

export async function requestConfirmationInformation(requirementId: string, membershipId: string, requestText: string) {
  return invoke('request_r1_confirmation_information', {
    p_requirement_id: requirementId, p_requested_from_membership_id: membershipId, p_request_text: requestText,
  })
}

export async function answerAssessmentInformationRequest(informationRequestId: string, responseText: string, evidenceReference?: string) {
  return object(await invoke('answer_r1_assessment_information_request', {
    p_information_request_id: informationRequestId, p_response_text: responseText, p_evidence_reference: evidenceReference || null,
  }))
}

export type InformationRequestTask = {
  request: Record<string, unknown>
  aiUseCase: Record<string, unknown>
  requester: string
  confirmationType: string
  confirmationDeepLinkPath: string
  canRespond: boolean
  capabilities: {
    canTransfer: boolean
    canRecall: boolean
    canWithdraw: boolean
    assignmentRevision: number
    eligibleRecipients: Record<string, unknown>[]
  }
}

export async function getInformationRequestTask(informationRequestId: string): Promise<InformationRequestTask> {
  const row = object(await invoke('get_r1_information_request_task', { p_information_request_id: informationRequestId }))
  return {
    request: object(row.request),
    aiUseCase: object(row.aiUseCase),
    requester: text(row.requester),
    confirmationType: text(row.confirmationType),
    confirmationDeepLinkPath: text(row.confirmationDeepLinkPath),
    canRespond: booleanValue(row.canRespond),
    capabilities: {
      canTransfer: booleanValue(object(row.capabilities).canTransfer),
      canRecall: booleanValue(object(row.capabilities).canRecall),
      canWithdraw: booleanValue(object(row.capabilities).canWithdraw),
      assignmentRevision: numberValue(object(row.capabilities).assignmentRevision),
      eligibleRecipients: objects(object(row.capabilities).eligibleRecipients),
    },
  }
}

export type ConfirmationCoordination = {
  canCoordinateConfirmation: boolean
  canTransferConfirmation: boolean
  canRecallConfirmation: boolean
  assignmentRevision: number
  assignmentState: string
  canManageInformationRequests: boolean
  canCoordinateInformationRequestAssignments: boolean
  currentMembershipId: string
  eligibleReviewers: Record<string, unknown>[]
  eligibleRecipients: Record<string, unknown>[]
  history: Record<string, unknown>[]
}

export async function getConfirmationCoordination(requirementId: string): Promise<ConfirmationCoordination> {
  const row = object(await invoke('get_r1_confirmation_coordination', { p_requirement_id: requirementId }))
  return {
    canCoordinateConfirmation: booleanValue(row.canCoordinateConfirmation),
    canTransferConfirmation: booleanValue(row.canTransferConfirmation),
    canRecallConfirmation: booleanValue(row.canRecallConfirmation),
    assignmentRevision: Number(row.assignmentRevision) || 0,
    assignmentState: text(row.assignmentState),
    canManageInformationRequests: booleanValue(row.canManageInformationRequests),
    canCoordinateInformationRequestAssignments: booleanValue(row.canCoordinateInformationRequestAssignments),
    currentMembershipId: text(row.currentMembershipId),
    eligibleReviewers: objects(row.eligibleReviewers),
    eligibleRecipients: objects(row.eligibleRecipients),
    history: objects(row.history),
  }
}

export async function reassignConfirmation(
  requirementId: string,
  expectedAssignmentRevision: number,
  membershipId: string,
  reason: string,
) {
  return object(await invoke('reassign_r1_confirmation', {
    p_requirement_id: requirementId,
    p_expected_assignment_revision: expectedAssignmentRevision,
    p_membership_id: membershipId,
    p_reason: reason,
  }))
}

export async function recallConfirmation(
  requirementId: string,
  expectedAssignmentRevision: number,
  reason: string,
) {
  return object(await invoke('recall_r1_confirmation', {
    p_requirement_id: requirementId,
    p_expected_assignment_revision: expectedAssignmentRevision,
    p_reason: reason,
  }))
}

export async function withdrawConfirmation(requirementId: string, reason: string, explanation?: string) {
  return object(await invoke('cancel_r1_confirmation', {
    p_requirement_id: requirementId, p_reason: reason, p_explanation: explanation || null,
  }))
}

export async function startConfirmationReassessment(requirementId: string, reason: string) {
  return object(await invoke('start_r1_confirmation_reassessment', {
    p_requirement_id: requirementId,
    p_reason: reason,
  }))
}

export async function reassignInformationRequest(
  informationRequestId: string,
  expectedAssignmentRevision: number,
  membershipId: string,
  reason: string,
) {
  return object(await invoke('reassign_r1_assessment_information_request', {
    p_information_request_id: informationRequestId,
    p_expected_assignment_revision: expectedAssignmentRevision,
    p_membership_id: membershipId,
    p_reason: reason,
  }))
}

export async function recallInformationRequest(
  informationRequestId: string,
  expectedAssignmentRevision: number,
  reason: string,
) {
  return object(await invoke('recall_r1_assessment_information_request', {
    p_information_request_id: informationRequestId,
    p_expected_assignment_revision: expectedAssignmentRevision,
    p_reason: reason,
  }))
}

export async function withdrawInformationRequest(informationRequestId: string, reason: string, explanation?: string) {
  return object(await invoke('cancel_r1_assessment_information_request', {
    p_information_request_id: informationRequestId, p_reason: reason, p_explanation: explanation || null,
  }))
}

export async function respondConfirmation(
  requirementId: string,
  decision: string,
  fields: { confirmedValue?: unknown; comments?: string; conditions?: string; reason?: string; evidenceReference?: string },
) {
  return object(await invoke('respond_r1_confirmation', {
    p_requirement_id: requirementId,
    p_decision: decision,
    p_confirmed_value: fields.confirmedValue ?? null,
    p_comments: fields.comments || null,
    p_conditions: fields.conditions || null,
    p_reason: fields.reason || null,
    p_evidence_reference: fields.evidenceReference || null,
  }))
}
